import { Plugin, PluginKey } from 'prosemirror-state';
import dryEditorClass from './DryEditorClass';
import constants from '../../constants';

const { EDITOR_COMMAND_NAME_MAP } = constants;

export const slashMenuKey = new PluginKey('slashMenu');

// ─── Command definitions ──────────────────────────────────────────────────────

const SLASH_COMMANDS = [
  {
    id: 'paragraph',
    label: 'Paragraph',
    description: 'Normal text',
    icon: 'notes',
    keywords: ['paragraph', 'text', 'normal', 'p'],
    execute: () => dryEditorClass.execCommands([{ commandName: EDITOR_COMMAND_NAME_MAP.heading, options: { level: 0 } }]),
  },
  {
    id: 'h1',
    label: 'Heading 1',
    description: 'Large section heading',
    icon: 'title',
    keywords: ['h1', 'heading', 'title', 'header'],
    execute: () => dryEditorClass.execCommands([{ commandName: EDITOR_COMMAND_NAME_MAP.heading, options: { level: 1 } }]),
  },
  {
    id: 'h2',
    label: 'Heading 2',
    description: 'Medium section heading',
    icon: 'title',
    keywords: ['h2', 'heading', 'header'],
    execute: () => dryEditorClass.execCommands([{ commandName: EDITOR_COMMAND_NAME_MAP.heading, options: { level: 2 } }]),
  },
  {
    id: 'h3',
    label: 'Heading 3',
    description: 'Small section heading',
    icon: 'title',
    keywords: ['h3', 'heading', 'header'],
    execute: () => dryEditorClass.execCommands([{ commandName: EDITOR_COMMAND_NAME_MAP.heading, options: { level: 3 } }]),
  },
  {
    id: 'h4',
    label: 'Heading 4',
    description: 'Smaller section heading',
    icon: 'title',
    keywords: ['h4', 'heading', 'header'],
    execute: () => dryEditorClass.execCommands([{ commandName: EDITOR_COMMAND_NAME_MAP.heading, options: { level: 4 } }]),
  },
  {
    id: 'bullet_list',
    label: 'Bullet list',
    description: 'Unordered list of items',
    icon: 'format_list_bulleted',
    keywords: ['bullet', 'list', 'ul', 'unordered'],
    execute: () => dryEditorClass.execCommands([{ commandName: EDITOR_COMMAND_NAME_MAP.unordered_list }]),
  },
  {
    id: 'numbered_list',
    label: 'Numbered list',
    description: 'Ordered list of items',
    icon: 'format_list_numbered',
    keywords: ['numbered', 'list', 'ol', 'ordered', '1'],
    execute: () => dryEditorClass.execCommands([{ commandName: EDITOR_COMMAND_NAME_MAP.ordered_list }]),
  },
  {
    id: 'checklist',
    label: 'Checklist',
    description: 'List of items with checkboxes',
    icon: 'checklist',
    keywords: ['checklist', 'todo', 'task', 'check', 'list'],
    execute: () => dryEditorClass.execCommands([{ commandName: EDITOR_COMMAND_NAME_MAP.check_list }]),
  },
  {
    id: 'checkbox',
    label: 'Checkbox',
    description: 'To-do item with a checkbox',
    icon: 'check_box_outline_blank',
    keywords: ['todo', 'check', 'task', 'checkbox'],
    execute: () => dryEditorClass.execCommands([{ commandName: EDITOR_COMMAND_NAME_MAP.checkbox }]),
  },
  {
    id: 'code_block',
    label: 'Code block',
    description: 'Block of preformatted code',
    icon: 'code',
    keywords: ['code', 'pre', 'block', 'snippet'],
    execute: () => dryEditorClass.execCommands([{ commandName: EDITOR_COMMAND_NAME_MAP.code_block }]),
  },
  {
    id: 'divider',
    label: 'Divider',
    description: 'Horizontal separator line',
    icon: 'horizontal_rule',
    keywords: ['hr', 'divider', 'rule', 'separator', 'line'],
    execute: () => dryEditorClass.execCommands([{ commandName: EDITOR_COMMAND_NAME_MAP.horizontal_rule }]),
  },
];

// ─── Filter helpers ───────────────────────────────────────────────────────────

function filterCommands(query) {
  if (!query) return SLASH_COMMANDS;
  const q = query.toLowerCase();
  return SLASH_COMMANDS.filter(
    (cmd) =>
      cmd.label.toLowerCase().includes(q) ||
      cmd.keywords.some((k) => k.includes(q))
  );
}

// ─── Menu DOM ─────────────────────────────────────────────────────────────────

const MENU_ID = 'dry-slash-menu';
let menuEl = null;
let _onSelectCommand = null;

function getOrCreateMenu() {
  if (menuEl) return menuEl;

  menuEl = document.createElement('div');
  menuEl.id = MENU_ID;
  menuEl.setAttribute('role', 'listbox');
  menuEl.setAttribute('aria-label', 'Insert block');
  Object.assign(menuEl.style, {
    position: 'fixed',
    zIndex: '10000',
    display: 'none',
    flexDirection: 'column',
    background: '#252526',
    border: '1px solid #454545',
    borderRadius: '6px',
    padding: '4px 0',
    boxShadow: '0 4px 20px rgba(0,0,0,0.55)',
    minWidth: '250px',
    maxHeight: '320px',
    overflowY: 'auto',
  });

  document.body.appendChild(menuEl);
  return menuEl;
}

function renderMenuItems(commands, selectedIdx) {
  const el = getOrCreateMenu();
  el.innerHTML = '';

  if (commands.length === 0) {
    const empty = document.createElement('div');
    empty.textContent = 'No results';
    Object.assign(empty.style, {
      padding: '8px 14px',
      fontSize: '0.8125rem',
      color: '#858585',
    });
    el.appendChild(empty);
    return;
  }

  commands.forEach((cmd, i) => {
    const item = document.createElement('div');
    item.setAttribute('role', 'option');
    item.setAttribute('aria-selected', i === selectedIdx ? 'true' : 'false');
    Object.assign(item.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '7px 12px',
      cursor: 'pointer',
      borderRadius: '4px',
      margin: '1px 4px',
      background: i === selectedIdx ? '#37373d' : 'none',
    });

    const icon = document.createElement('i');
    icon.className = 'material-icons';
    icon.textContent = cmd.icon;
    Object.assign(icon.style, {
      fontSize: '18px',
      color: i === selectedIdx ? '#9cdcfe' : '#858585',
      flexShrink: '0',
      width: '20px',
      textAlign: 'center',
    });
    item.appendChild(icon);

    const textDiv = document.createElement('div');
    Object.assign(textDiv.style, { display: 'flex', flexDirection: 'column', minWidth: '0' });

    const label = document.createElement('span');
    label.textContent = cmd.label;
    Object.assign(label.style, {
      fontSize: '0.875rem',
      color: '#cccccc',
      fontWeight: '500',
      lineHeight: '1.3',
    });

    const desc = document.createElement('span');
    desc.textContent = cmd.description;
    Object.assign(desc.style, {
      fontSize: '0.75rem',
      color: '#858585',
      lineHeight: '1.3',
    });

    textDiv.appendChild(label);
    textDiv.appendChild(desc);
    item.appendChild(textDiv);

    item.addEventListener('mouseenter', () => {
      renderMenuItems(commands, i);
    });

    // mousedown+preventDefault keeps editor focus; the actual execute happens here
    item.addEventListener('mousedown', (e) => {
      e.preventDefault();
      if (_onSelectCommand) _onSelectCommand(cmd);
    });

    el.appendChild(item);
  });
}

function showMenu(x, y) {
  const el = getOrCreateMenu();
  el.style.display = 'flex';

  // Flip up if not enough space below
  const menuH = el.scrollHeight || 200;
  const top = y + menuH + 8 > window.innerHeight ? y - menuH - 4 : y + 4;
  const left = Math.min(x, window.innerWidth - 260);
  el.style.top = `${Math.max(0, top)}px`;
  el.style.left = `${Math.max(0, left)}px`;
}

function hideMenu() {
  if (menuEl) menuEl.style.display = 'none';
}

function scrollSelectedIntoView() {
  if (!menuEl) return;
  const selected = menuEl.querySelector('[aria-selected="true"]');
  if (selected) selected.scrollIntoView({ block: 'nearest' });
}

// ─── Plugin ───────────────────────────────────────────────────────────────────

export const createSlashCommandPlugin = () => {
  return new Plugin({
    key: slashMenuKey,

    state: {
      init() {
        return { active: false, from: -1, filter: '', selectedIdx: 0 };
      },
      apply(tr, prev) {
        const meta = tr.getMeta(slashMenuKey);
        if (meta !== undefined) return meta;

        if (!prev.active) {
          // Detect '/' typed at the start of a block (no other text before it)
          if (!tr.docChanged) return prev;
          const $cursor = tr.selection.$cursor;
          if (!$cursor) return prev;

          const pos = $cursor.pos;
          if (pos < 1) return prev;

          const charBefore = tr.doc.textBetween(pos - 1, pos);
          if (charBefore !== '/') return prev;

          // Only activate at the very start of a block (no preceding text)
          const blockStart = $cursor.start();
          const textBeforeSlash = tr.doc.textBetween(blockStart, pos - 1);
          if (textBeforeSlash !== '') return prev;

          return { active: true, from: pos - 1, filter: '', selectedIdx: 0 };
        }

        // Already active — update filter or deactivate
        if (tr.docChanged || tr.selectionSet) {
          const $cursor = tr.selection.$cursor;
          if (!$cursor) return { active: false, from: -1, filter: '', selectedIdx: 0 };

          const mappedFrom = tr.mapping.map(prev.from);
          const cursorPos = $cursor.pos;

          if (cursorPos < mappedFrom) {
            return { active: false, from: -1, filter: '', selectedIdx: 0 };
          }

          // Read text from slash pos to cursor
          let text;
          try {
            text = tr.doc.textBetween(mappedFrom, cursorPos);
          } catch (_) {
            return { active: false, from: -1, filter: '', selectedIdx: 0 };
          }

          if (!text.startsWith('/')) {
            return { active: false, from: -1, filter: '', selectedIdx: 0 };
          }

          const filter = text.slice(1);
          const commands = filterCommands(filter);
          const selectedIdx = Math.min(prev.selectedIdx, Math.max(0, commands.length - 1));
          return { active: true, from: mappedFrom, filter, selectedIdx };
        }

        return prev;
      },
    },

    view() {
      return {
        update(view) {
          const state = slashMenuKey.getState(view.state);

          if (!state.active) {
            hideMenu();
            _onSelectCommand = null;
            return;
          }

          const commands = filterCommands(state.filter);

          // Position menu at cursor
          let coords;
          try {
            coords = view.coordsAtPos(state.from);
          } catch (_) {
            hideMenu();
            return;
          }

          _onSelectCommand = (cmd) => {
            executeCommand(view, slashMenuKey.getState(view.state), cmd);
          };

          renderMenuItems(commands, state.selectedIdx);
          showMenu(coords.left, coords.bottom);
          scrollSelectedIntoView();
        },
        destroy() {
          hideMenu();
          _onSelectCommand = null;
        },
      };
    },

    props: {
      handleKeyDown(view, event) {
        const state = slashMenuKey.getState(view.state);
        if (!state.active) return false;

        const commands = filterCommands(state.filter);

        if (event.key === 'Escape') {
          view.dispatch(
            view.state.tr.setMeta(slashMenuKey, {
              active: false, from: -1, filter: '', selectedIdx: 0,
            })
          );
          return true;
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          const next = (state.selectedIdx + 1) % Math.max(1, commands.length);
          view.dispatch(
            view.state.tr.setMeta(slashMenuKey, { ...state, selectedIdx: next })
          );
          return true;
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault();
          const prev = (state.selectedIdx - 1 + Math.max(1, commands.length)) % Math.max(1, commands.length);
          view.dispatch(
            view.state.tr.setMeta(slashMenuKey, { ...state, selectedIdx: prev })
          );
          return true;
        }

        if (event.key === 'Enter' || event.key === 'Tab') {
          if (commands.length > 0) {
            event.preventDefault();
            executeCommand(view, state, commands[state.selectedIdx]);
            return true;
          }
          return false;
        }

        return false;
      },
    },
  });
};

// ─── Execute ──────────────────────────────────────────────────────────────────

function executeCommand(view, state, cmd) {
  const { from } = state;
  const cursorPos = view.state.selection.to;

  // Delete the /query text, then close the menu
  const tr = view.state.tr
    .delete(from, cursorPos)
    .setMeta(slashMenuKey, { active: false, from: -1, filter: '', selectedIdx: 0 });
  view.dispatch(tr);

  // Run the formatting command against the updated state
  cmd.execute();
}
